import { useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Lock, Sparkles, CircleCheck, HandHelping } from "lucide-react";
import ClientShell from "./ClientShell";
import { useMe, useWorkspacePracticeLayer } from "../../lib/rehearsalRoomQueries";
import { usePracticeAssignments, useRequestPractice, useStartAssignment } from "../../lib/practiceQueries";
import { useRecommendationForLayer } from "../../lib/recommendationQueries";
import { WhyThisButton } from "./shared/WhyThisExplanation";

const RENDERER_LABEL = {
  content_viewer: "Guide", checklist: "Checklist", guided_practice: "Guided Practice", reflection: "Reflection",
};

// Master v5 Prompt 9 — the real Practice-layer destination a journey
// node opens. Locked-layer copy is unchanged from Prompt 7; the
// "unlocked" branch now surfaces real Assignment/InteractionInstance
// state (never fabricated) and, when nothing is eligible yet, the
// idempotent PracticeRequest fallback — never the shared Room Runtime
// itself (ordinary shell navigation stays visible here; the Room opens
// only after Start/Resume).
export default function PracticeLayerRoute() {
  const { workspaceId, layerKey } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get("returnTo");
  const { data: me } = useMe();
  const { data, isError } = useWorkspacePracticeLayer(workspaceId, layerKey);

  return (
    <ClientShell>
      <div className="max-w-2xl" data-testid="practice-layer-route">
        <button
          onClick={() => navigate(returnTo === "home" ? `/rehearsal-room/w/${workspaceId}/home` : -1)}
          className="flex items-center gap-1.5 text-sm text-ink-soft hover:text-terracotta transition-colors mb-6"
          data-testid="practice-layer-back-link"
        >
          <ArrowLeft size={14} /> Back to Home
        </button>

        {!data && !isError && <p className="text-ink-soft" data-testid="practice-layer-loading">Loading…</p>}

        {isError && (
          <div className="border border-ink/15 bg-white/40 rounded-lg p-6 text-ink-soft" data-testid="practice-layer-not-found">
            This practice layer isn't available for you right now.
          </div>
        )}

        {data && data.state === "locked" && (
          <div className="border border-ink/15 bg-white/40 rounded-lg p-8" data-testid="practice-layer-locked-explanation">
            <Lock size={22} className="text-ink-soft mb-4" />
            <h1 className="font-serif text-2xl text-ink mb-2">{data.label}</h1>
            <p className="text-ink-soft">
              This layer isn't unlocked yet. Your coach will open it for you when you're ready — no
              content is shown here until then.
            </p>
          </div>
        )}

        {data && data.state !== "locked" && me && (
          <div>
            <h1 className="font-serif text-2xl text-ink mb-1" data-testid="practice-layer-title">{data.label}</h1>
            {data.helper_text && <p className="text-ink-soft mb-6" data-testid="practice-layer-helper-text">{data.helper_text}</p>}
            <RecommendedPickBanner workspaceId={workspaceId} layerKey={layerKey} />
            <PracticeAssignmentsPanel workspaceId={workspaceId} layerKey={layerKey} personId={me.per_id} navigate={navigate} />
          </div>
        )}
      </div>
    </ClientShell>
  );
}

// Master v5 Prompt 23 — a small "why this one" note above the layer's
// assignment list. Only ever a `published` pick surfaces here (never
// `pending_coach_review`); the specific card it refers to is highlighted
// by PracticeAssignmentsPanel matching on lib_id.
function RecommendedPickBanner({ workspaceId, layerKey }) {
  const { data } = useRecommendationForLayer(workspaceId, layerKey);
  if (!data || data.status !== "published" || !data.rationale_summary) return null;
  return (
    <div className="text-sm text-terracotta bg-terracotta/5 border border-terracotta/20 rounded-md px-3 py-2 mb-4 flex items-center justify-between gap-2" data-testid="practice-layer-recommendation-banner">
      <span className="flex items-center gap-2"><Sparkles size={14} className="shrink-0" /> {data.rationale_summary}</span>
      <WhyThisButton workspaceId={workspaceId} recId={data.rec_id} />
    </div>
  );
}

function PracticeAssignmentsPanel({ workspaceId, layerKey, personId, navigate }) {
  const { data, isPending, isError } = usePracticeAssignments(workspaceId, layerKey, personId);
  const requestPractice = useRequestPractice(workspaceId, layerKey);
  const startAssignment = useStartAssignment(workspaceId);
  const [startingId, setStartingId] = useState(null);

  if (isError) {
    return <p className="text-terracotta text-sm" data-testid="practice-layer-assignments-error">Practice content couldn't be loaded.</p>;
  }
  if (isPending || !data) {
    return <p className="text-ink-soft text-sm" data-testid="practice-layer-assignments-loading">Loading practice content…</p>;
  }

  async function handleStart(asgId) {
    setStartingId(asgId);
    try {
      const result = await startAssignment.mutateAsync(asgId);
      navigate(`/rehearsal-room/w/${workspaceId}/room/${result.launch_id}`);
    } finally {
      setStartingId(null);
    }
  }

  if (data.items.length === 0) {
    return (
      <div className="border border-ink/15 bg-white/40 rounded-lg p-8" data-testid="practice-layer-empty-state">
        <Sparkles size={20} className="text-ink-soft mb-3" />
        <p className="text-ink mb-1 font-medium">No exercises assigned yet</p>
        <p className="text-ink-soft text-sm mb-5">
          Your coach hasn't assigned an exercise for this layer yet. Let them know you're ready to practise.
        </p>
        {data.practice_request ? (
          <p className="inline-flex items-center gap-2 text-sm text-ink-blue" data-testid="practice-layer-request-sent-badge">
            <CircleCheck size={15} /> Your coach has been notified — a request is already open.
          </p>
        ) : (
          <button
            onClick={() => requestPractice.mutate()}
            disabled={requestPractice.isPending}
            className="inline-flex items-center gap-2 bg-terracotta text-paper text-sm font-medium px-4 py-2 rounded-md hover:bg-terracotta/90 disabled:opacity-50"
            data-testid="practice-layer-request-practice-btn"
          >
            <HandHelping size={15} /> {requestPractice.isPending ? "Sending…" : "Request practice for this layer"}
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="practice-layer-assignments-list">
      {data.items.map(({ assignment, instance }) => {
        const isCompleted = instance?.state === "completed";
        const isInProgress = instance?.state === "in_progress";
        const buttonLabel = isCompleted ? "View" : isInProgress ? "Resume" : "Start";
        return (
          <div key={assignment.asg_id} className="border border-ink/15 bg-white/50 rounded-lg p-5 flex items-center justify-between gap-4" data-testid={`practice-assignment-card-${assignment.asg_id}`}>
            <div className="min-w-0">
              <p className="text-xs uppercase tracking-wide text-ink-soft mb-1">{RENDERER_LABEL[assignment.renderer_key] || assignment.renderer_key}</p>
              <p className="font-serif text-lg text-ink truncate">{assignment.title_snapshot}</p>
              {isCompleted && (
                <p className="text-xs text-ink-blue mt-1 flex items-center gap-1" data-testid={`practice-assignment-card-${assignment.asg_id}-completed-badge`}>
                  <CircleCheck size={12} />
                  {assignment.renderer_key === "content_viewer"
                    ? "Done"
                    : `Attempt #${instance.current_attempt_number} submitted`}
                </p>
              )}
              {isInProgress && (
                <p className="text-xs text-mustard-dark mt-1" data-testid={`practice-assignment-card-${assignment.asg_id}-in-progress-badge`}>In progress</p>
              )}
            </div>
            <button
              onClick={() => handleStart(assignment.asg_id)}
              disabled={startingId === assignment.asg_id}
              className="shrink-0 text-sm font-medium text-terracotta hover:text-terracotta/80 disabled:opacity-50"
              data-testid={`practice-assignment-card-${assignment.asg_id}-start-btn`}
            >
              {startingId === assignment.asg_id ? "Opening…" : buttonLabel}
            </button>
          </div>
        );
      })}
    </div>
  );
}

